// src/pages/groups/GroupDetails.jsx
// المدرس: الطلبة (إضافة/إزالة/نقل/تقدم/تقرير PDF) + الاختبارات + نظرة عامة + المتصدرين + الخاملين
// الأدمن: الطلبة (عرض + إضافة + تقرير PDF) + الاختبارات + نقل المجموعة لمدرس تاني + حذف
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowRight, Eye, FileText, Pencil, Plus, Search, Trash2, UserMinus, X, ArrowLeftRight } from "lucide-react";
import { useAuthStore } from "../../store/authstore";
import { groupService, getErrorMessage } from "../../services/groupService";
import { quizService } from "../../services/quizService";
import GroupFormModal from "./GroupFormModal";
import GroupQuizzesTab, { StudentQuizResultsModal } from "./GroupQuizzesTab";
import StudentProgressDrawer from "./StudentProgressDrawer";
import {
  ActivityBar,
  Avatar,
  ConfirmDialog,
  DaysSeg,
  EmptyState,
  Legend,
  Modal,
  Pagination,
  ProgressBar,
  Seg,
  Spinner,
  Stat,
  arDays,
  fmt,
  formatRelative,
  recencyTone,
  useDebounce,
  useGroupStyles,
  useToast,
} from "./groupsShared";

const ORDERINGS = [
  ["name", "الاسم"],
  ["-score", "الأعلى نقاطاً"],
  ["score", "الأقل نقاطاً"],
  ["-activity", "الأحدث نشاطاً"],
  ["activity", "الأقدم نشاطاً"],
];

/* ───────────────────────── إضافة طلبة ───────────────────────── */

function AddStudentsModal({ groupId, isAdmin, onClose, onAdded }) {
  const [term, setTerm] = useState("");
  const q = useDebounce(term, 300);
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState(new Map());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const reqRef = useRef(0);

  useEffect(() => {
    if (q.trim().length < 2) {
      setResults([]);
      return;
    }
    const id = ++reqRef.current;
    setSearching(true);
    setError("");
    const search = isAdmin ? groupService.adminSearchStudents : groupService.searchStudents;
    search(q.trim(), groupId)
      .then((res) => id === reqRef.current && setResults(res.data))
      .catch((e) => id === reqRef.current && setError(getErrorMessage(e)))
      .finally(() => id === reqRef.current && setSearching(false));
  }, [q, groupId, isAdmin]);

  const toggle = (s) =>
    setPicked((prev) => {
      const next = new Map(prev);
      if (next.has(s.id)) next.delete(s.id);
      else next.set(s.id, s);
      return next;
    });

  const submit = async () => {
    setSaving(true);
    setError("");
    try {
      const add = isAdmin ? groupService.adminAddStudents : groupService.addStudents;
      const res = await add(groupId, [...picked.keys()]);
      onAdded(res.message);
    } catch (e) {
      setError(getErrorMessage(e));
      setSaving(false);
    }
  };

  return (
    <Modal
      title="إضافة طلاب للمجموعة"
      onClose={onClose}
      width={520}
      footer={
        <>
          <button className="gp-btn gp-btn-primary" onClick={submit} disabled={saving || picked.size === 0}>
            {saving ? "جاري الإضافة..." : `إضافة ${picked.size || ""} طالب`.trim()}
          </button>
          <button className="gp-btn" onClick={onClose} disabled={saving}>
            إلغاء
          </button>
        </>
      }
    >
      {error && <div className="gp-error">{error}</div>}
      <div className="gp-search">
        <Search size={15} />
        <input
          className="gp-input"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder="ابحث باسم الطالب أو بريده الإلكتروني"
          aria-label="بحث عن طالب"
          autoFocus
        />
      </div>

      {picked.size > 0 && (
        <div className="gp-chips">
          {[...picked.values()].map((s) => (
            <span className="gp-chip" key={s.id}>
              <Avatar name={s.full_name} src={s.profile_picture_url} size={22} />
              {s.full_name}
              <button onClick={() => toggle(s)} aria-label={`إزالة ${s.full_name} من التحديد`}>
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      )}

      {q.trim().length < 2 ? (
        <p className="gp-muted" style={{ marginTop: 14 }}>
          اكتب حرفين على الأقل لبدء البحث. الطلاب الموجودون في المجموعة لن يظهروا في النتائج.
        </p>
      ) : searching ? (
        <Spinner />
      ) : results.length === 0 ? (
        <p className="gp-muted" style={{ marginTop: 14 }}>
          لا يوجد طلاب مطابقون للبحث.
        </p>
      ) : (
        <div className="gp-pick">
          {results.map((s) => (
            <button key={s.id} type="button" aria-pressed={picked.has(s.id)} onClick={() => toggle(s)}>
              <Avatar name={s.full_name} src={s.profile_picture_url} size={32} />
              <span className="gp-who" style={{ display: "block", minWidth: 0 }}>
                <b>{s.full_name}</b>
                <small>{s.email}</small>
              </span>
            </button>
          ))}
        </div>
      )}
    </Modal>
  );
}

/* ───────────────────────── نقل / نسخ ───────────────────────── */

function MoveStudentsModal({ groupId, ids, onClose, onDone }) {
  const [groups, setGroups] = useState(null);
  const [target, setTarget] = useState(null);
  const [action, setAction] = useState("move");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    groupService
      .listGroups({ page_size: 100 })
      .then((res) => setGroups(res.data.filter((g) => g.id !== Number(groupId))))
      .catch((e) => {
        setError(getErrorMessage(e));
        setGroups([]);
      });
  }, [groupId]);

  const submit = async () => {
    setSaving(true);
    setError("");
    try {
      const res = await groupService.moveStudents(groupId, {
        student_ids: ids,
        target_group_id: target,
        action,
      });
      onDone(res.message);
    } catch (e) {
      setError(getErrorMessage(e));
      setSaving(false);
    }
  };

  return (
    <Modal
      title={`نقل أو نسخ ${ids.length} طالب`}
      onClose={onClose}
      footer={
        <>
          <button className="gp-btn gp-btn-primary" onClick={submit} disabled={saving || !target}>
            {saving ? "جاري التنفيذ..." : action === "move" ? "نقل الطلاب" : "نسخ الطلاب"}
          </button>
          <button className="gp-btn" onClick={onClose} disabled={saving}>
            إلغاء
          </button>
        </>
      }
    >
      {error && <div className="gp-error">{error}</div>}
      <Seg
        label="نوع العملية"
        value={action}
        onChange={setAction}
        options={[
          ["move", "نقل (يُزالون من هنا)"],
          ["copy", "نسخ (يبقون هنا)"],
        ]}
      />
      {groups === null ? (
        <Spinner />
      ) : groups.length === 0 ? (
        <p className="gp-muted" style={{ marginTop: 14 }}>
          ليس لديك مجموعات أخرى. أنشئ مجموعة جديدة أولاً.
        </p>
      ) : (
        <div className="gp-pick">
          {groups.map((g) => (
            <button key={g.id} type="button" aria-pressed={target === g.id} onClick={() => setTarget(g.id)}>
              <span className="gp-who" style={{ display: "block", minWidth: 0 }}>
                <b>{g.name}</b>
                <small>{g.students_count} طالب</small>
              </span>
            </button>
          ))}
        </div>
      )}
    </Modal>
  );
}

/* ───────────────────────── تقرير الطالب (PDF) ───────────────────────── */

function StudentReportModal({ groupId, isAdmin, student, onClose }) {
  const [type, setType] = useState("platform");
  const [languages, setLanguages] = useState(null); // null = جاري التحميل
  const [language, setLanguage] = useState("");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");

  // اللغات اللي الطالب له نشاط فيها
  useEffect(() => {
    let off = false;
    const fetchOptions = isAdmin
      ? groupService.adminGetStudentReportOptions
      : groupService.getStudentReportOptions;
    fetchOptions(groupId, student.id)
      .then((res) => {
        if (off) return;
        const list = res.data?.languages || [];
        setLanguages(list);
        setLanguage(list[0]?.code || "");
      })
      .catch(() => !off && setLanguages([]));
    return () => {
      off = true;
    };
  }, [groupId, isAdmin, student.id]);

  const needsLanguage = type === "language";
  const blocked = needsLanguage && !language;

  const openReport = async (lang) => {
    // بنفتح التاب فوراً (مع ضغطة المستخدم) عشان المتصفح ما يبلوكش البوب أب
    const win = window.open("", "_blank");
    setBusy(lang);
    setError("");
    try {
      const fetchReport = isAdmin ? groupService.adminGetStudentReport : groupService.getStudentReport;
      const params = { lang, type };
      if (needsLanguage) params.language = language;
      const data = await fetchReport(groupId, student.id, params);
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      if (win) {
        win.location.href = url;
      } else {
        // لو البوب أب اتبلوك، نزّل الملف
        const a = document.createElement("a");
        a.href = url;
        a.download = `report_${needsLanguage ? `${language}_` : ""}${lang}_${student.id}.pdf`;
        a.click();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      if (win) win.close();
      setError(getErrorMessage(e));
    } finally {
      setBusy("");
    }
  };

  return (
    <Modal
      title={`تقرير ${student.full_name}`}
      onClose={onClose}
      width={460}
      footer={
        <button className="gp-btn" onClick={onClose}>
          إغلاق
        </button>
      }
    >
      {error && <div className="gp-error">{error}</div>}
      <Seg
        label="نوع التقرير"
        value={type}
        onChange={setType}
        options={[
          ["platform", "IELTS / STEP / ESP / General"],
          ["academy", "الأكاديمية"],
          ["language", "اللغات"],
        ]}
      />

      {needsLanguage &&
        (languages === null ? (
          <Spinner />
        ) : languages.length === 0 ? (
          <p className="gp-muted" style={{ marginTop: 14 }}>
            هذا الطالب ليس له نشاط في أي لغة حتى الآن.
          </p>
        ) : (
          <div className="gp-field" style={{ marginTop: 14 }}>
            <label htmlFor="gp-report-language">اللغة</label>
            <select
              id="gp-report-language"
              className="gp-select"
              style={{ width: "100%" }}
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
            >
              {languages.map((l) => (
                <option key={l.code} value={l.code}>
                  {l.native_name && l.native_name !== l.name ? `${l.name} (${l.native_name})` : l.name}
                </option>
              ))}
            </select>
          </div>
        ))}

      <p className="gp-muted" style={{ margin: "14px 0" }}>
        اختر لغة التقرير، وهيتفتح PDF في تاب جديد.
      </p>
      <div className="gp-actions">
        <button className="gp-btn gp-btn-primary" onClick={() => openReport("ar")} disabled={!!busy || blocked}>
          <FileText size={15} /> {busy === "ar" ? "جاري التحضير..." : "تقرير بالعربي"}
        </button>
        <button className="gp-btn gp-btn-primary" onClick={() => openReport("en")} disabled={!!busy || blocked}>
          <FileText size={15} /> {busy === "en" ? "Preparing..." : "English report"}
        </button>
      </div>
    </Modal>
  );
}

/* ───────────────────────── تبويب الطلبة ───────────────────────── */

// أعمدة جدول الطلبة (مضبوطة هنا عشان ما نعتمد على groupsShared)
// المدرس: checkbox + الطالب + النقاط + البرامج + آخر نشاط + الاختبارات + الإجراءات = 7
// الأدمن: نفس الشيء بدون checkbox = 6
// على الموبايل: الأعمدة اللي عليها gp-hide-sm بتتخفي، فبيفضل الطالب + الإجراءات
const STUDENTS_GRID_CSS = `
.gp-table .gp-students-row{display:grid!important;align-items:center;column-gap:12px;grid-template-columns:36px minmax(0,2.4fr) .8fr .8fr 1.1fr 1.2fr 112px!important}
.gp-table .gp-students-row.no-check{grid-template-columns:minmax(0,2.4fr) .8fr .8fr 1.1fr 1.2fr 80px!important}
.gp-students-row .gp-row-actions{display:flex;gap:6px;justify-content:flex-start}
@media (max-width:720px){
.gp-table .gp-students-row{grid-template-columns:36px minmax(0,1fr) auto!important}
.gp-table .gp-students-row.no-check{grid-template-columns:minmax(0,1fr) auto!important}
}`;

function StudentsTab({ groupId, isAdmin, onChanged, notify }) {
  const [search, setSearch] = useState("");
  const q = useDebounce(search);
  const [ordering, setOrdering] = useState("name");
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ count: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [addOpen, setAddOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null); // student | "bulk"
  const [removing, setRemoving] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [reportFor, setReportFor] = useState(null);
  const [quizMap, setQuizMap] = useState(null); // { [student.id]: {quizzes_taken, average_percentage} }
  const [quizReload, setQuizReload] = useState(0);
  const [quizFor, setQuizFor] = useState(null);
  const reqRef = useRef(0);

  const load = useCallback(async () => {
    const id = ++reqRef.current;
    setLoading(true);
    setError("");
    try {
      const params = { page, page_size: 15, ordering };
      if (q.trim()) params.search = q.trim();
      const res = isAdmin
        ? await groupService.adminGroupStudents(groupId, params)
        : await groupService.listGroupStudents(groupId, params);
      if (id !== reqRef.current) return;
      setRows(res.data);
      setMeta({ count: res.count, totalPages: res.total_pages });
    } catch (e) {
      if (id !== reqRef.current) return;
      if (e?.response?.status === 404 && page > 1) {
        setPage(1);
        return;
      }
      setError(getErrorMessage(e));
    } finally {
      if (id === reqRef.current) setLoading(false);
    }
  }, [groupId, isAdmin, page, q, ordering]);

  useEffect(() => {
    load();
  }, [load]);

  // ملخص الاختبارات لكل طلبة المجموعة (مرة واحدة، بيتربط بالصفوف بالـ id)
  useEffect(() => {
    let off = false;
    quizService
      .groupQuizResults(groupId, isAdmin)
      .then((res) => !off && setQuizMap(Object.fromEntries(res.data.map((r) => [r.id, r]))))
      .catch(() => !off && setQuizMap({})); // لو فشل، العمود يبقى "-" وباقي الصفحة شغالة
    return () => {
      off = true;
    };
  }, [groupId, isAdmin, quizReload]);

  const allOnPage = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const toggleAll = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      rows.forEach((r) => (allOnPage ? next.delete(r.id) : next.add(r.id)));
      return next;
    });
  const toggleOne = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const afterChange = (message) => {
    if (message) notify(message);
    setSelected(new Set());
    load();
    setQuizReload((k) => k + 1);
    onChanged();
  };

  const doRemove = async () => {
    setRemoving(true);
    try {
      const res =
        removeTarget === "bulk"
          ? await groupService.bulkRemove(groupId, [...selected])
          : await groupService.removeStudent(groupId, removeTarget.id);
      setRemoveTarget(null);
      afterChange(res.message);
    } catch (e) {
      notify(getErrorMessage(e), "err");
      setRemoveTarget(null);
    } finally {
      setRemoving(false);
    }
  };

  const cols = isAdmin ? "gp-row gp-students-row no-check" : "gp-row gp-students-row";

  return (
    <>
      <div className="gp-toolbar">
        <div className="gp-search">
          <Search size={15} />
          <input
            className="gp-input"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="ابحث باسم الطالب أو بريده"
            aria-label="بحث في طلاب المجموعة"
          />
        </div>
        <select
          className="gp-select"
          value={ordering}
          onChange={(e) => {
            setOrdering(e.target.value);
            setPage(1);
          }}
          aria-label="ترتيب الطلاب"
        >
          {ORDERINGS.map(([v, label]) => (
            <option key={v} value={v}>
              {label}
            </option>
          ))}
        </select>
        <button className="gp-btn gp-btn-primary" onClick={() => setAddOpen(true)}>
          <Plus size={15} /> إضافة طلاب
        </button>
      </div>

      {selected.size > 0 && (
        <div className="gp-selbar">
          <b>تم تحديد {selected.size}</b>
          <button className="gp-btn gp-btn-sm" onClick={() => setMoveOpen(true)}>
            <ArrowLeftRight size={13} /> نقل / نسخ
          </button>
          <button className="gp-btn gp-btn-sm gp-btn-danger" onClick={() => setRemoveTarget("bulk")}>
            <UserMinus size={13} /> إزالة من المجموعة
          </button>
          <button className="gp-btn gp-btn-sm" onClick={() => setSelected(new Set())}>
            إلغاء التحديد
          </button>
        </div>
      )}

      {error && <div className="gp-error">{error}</div>}

      {loading && rows.length === 0 ? (
        <Spinner />
      ) : rows.length === 0 ? (
        <div className="gp-panel">
          {q.trim() ? (
            <EmptyState title="لا توجد نتائج" hint="لا يوجد طالب بهذا الاسم أو البريد في المجموعة." />
          ) : (
            <EmptyState
              title="المجموعة فارغة"
              hint={isAdmin ? "أضف طلاباً لهذه المجموعة ليتابعهم المدرس." : "أضف طلابك لتبدأ متابعة تقدمهم."}
              action={
                <button className="gp-btn gp-btn-primary" onClick={() => setAddOpen(true)}>
                  <Plus size={15} /> إضافة طلاب
                </button>
              }
            />
          )}
        </div>
      ) : (
        <div className="gp-panel gp-table" aria-busy={loading}>
          <style>{STUDENTS_GRID_CSS}</style>
          {/* عدد الأعمدة هنا لازم يطابق الصفوف: (checkbox للمدرس) + 5 أعمدة + عمود الإجراءات */}
          <div className={`${cols} head`}>
            {!isAdmin && (
              <input type="checkbox" checked={allOnPage} onChange={toggleAll} aria-label="تحديد كل طلاب الصفحة" />
            )}
            <span>الطالب</span>
            <span className="gp-hide-sm">النقاط</span>
            <span className="gp-hide-sm">البرامج</span>
            <span className="gp-hide-sm">آخر نشاط</span>
            <span className="gp-hide-sm">الاختبارات / المتوسط</span>
            <span>الإجراءات</span>
          </div>
          {rows.map((s) => {
            const sm = s.progress?.summary;
            const qz = quizMap?.[s.id];
            return (
              <div className={cols} key={s.id}>
                {!isAdmin && (
                  <input
                    type="checkbox"
                    checked={selected.has(s.id)}
                    onChange={() => toggleOne(s.id)}
                    aria-label={`تحديد ${s.full_name}`}
                  />
                )}
                <div className="gp-who">
                  <Avatar name={s.full_name} src={s.profile_picture_url} />
                  <div style={{ minWidth: 0 }}>
                    <b>{s.full_name}</b>
                    <small>{s.email}</small>
                  </div>
                </div>
                <span className="gp-num gp-hide-sm">{fmt(sm?.total_score)}</span>
                <span className="gp-num gp-hide-sm">{sm?.programs_started ?? 0}</span>
                <span className={`gp-recency gp-hide-sm ${recencyTone(sm?.last_activity)}`}>
                  {formatRelative(sm?.last_activity)}
                </span>
                {/* عمود الاختبارات / المتوسط */}
                {qz && qz.quizzes_taken > 0 ? (
                  <button
                    type="button"
                    className="gp-hide-sm gp-num"
                    onClick={() => setQuizFor(s)}
                    title="عرض اختبارات الطالب"
                    style={{
                      background: "none",
                      border: 0,
                      padding: 0,
                      cursor: "pointer",
                      font: "inherit",
                      color: "inherit",
                      textAlign: "start",
                      textDecoration: "underline dotted",
                    }}
                  >
                    {qz.quizzes_taken} · {qz.average_percentage}%
                  </button>
                ) : (
                  <span className="gp-muted gp-hide-sm">-</span>
                )}
                <div className="gp-row-actions">
                  {!isAdmin && (
                    <button className="gp-icon-btn" onClick={() => setViewing(s.id)} aria-label={`تقدم ${s.full_name}`}>
                      <Eye size={15} />
                    </button>
                  )}
                  <button className="gp-icon-btn" onClick={() => setReportFor(s)} aria-label={`تقرير ${s.full_name}`}>
                    <FileText size={15} />
                  </button>
                  {!isAdmin && (
                    <button
                      className="gp-icon-btn danger"
                      onClick={() => setRemoveTarget(s)}
                      aria-label={`إزالة ${s.full_name} من المجموعة`}
                    >
                      <UserMinus size={15} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Pagination page={page} totalPages={meta.totalPages} onChange={setPage} />

      {addOpen && (
        <AddStudentsModal
          groupId={groupId}
          isAdmin={isAdmin}
          onClose={() => setAddOpen(false)}
          onAdded={(msg) => {
            setAddOpen(false);
            afterChange(msg);
          }}
        />
      )}
      {moveOpen && (
        <MoveStudentsModal
          groupId={groupId}
          ids={[...selected]}
          onClose={() => setMoveOpen(false)}
          onDone={(msg) => {
            setMoveOpen(false);
            afterChange(msg);
          }}
        />
      )}
      {removeTarget && (
        <ConfirmDialog
          danger
          title="إزالة من المجموعة"
          message={
            removeTarget === "bulk"
              ? `هتتشال عضوية ${selected.size} طالب من المجموعة. حساباتهم وتقدمهم مش هيتأثروا.`
              : `هتتشال عضوية "${removeTarget.full_name}" من المجموعة. حسابه وتقدمه مش هيتأثروا.`
          }
          confirmLabel="إزالة"
          loading={removing}
          onConfirm={doRemove}
          onCancel={() => setRemoveTarget(null)}
        />
      )}
      {viewing && <StudentProgressDrawer groupId={groupId} studentId={viewing} onClose={() => setViewing(null)} />}
      {reportFor && (
        <StudentReportModal
          groupId={groupId}
          isAdmin={isAdmin}
          student={reportFor}
          onClose={() => setReportFor(null)}
        />
      )}
      {quizFor && (
        <StudentQuizResultsModal
          groupId={groupId}
          isAdmin={isAdmin}
          student={quizFor}
          onClose={() => setQuizFor(null)}
        />
      )}
    </>
  );
}

/* ───────────────────────── نظرة عامة ───────────────────────── */

function OverviewTab({ groupId }) {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let off = false;
    setData(null);
    setError("");
    groupService
      .getStats(groupId, days)
      .then((res) => !off && setData(res.data))
      .catch((e) => !off && setError(getErrorMessage(e)));
    return () => {
      off = true;
    };
  }, [groupId, days]);

  if (error) return <div className="gp-error">{error}</div>;
  if (!data) return <Spinner />;

  const withHistory = Math.max(0, data.inactive_students - data.never_active_students);
  const segments = [
    { tone: "ok", value: data.active_students, label: `نشطون خلال ${arDays(days)}` },
    { tone: "warn", value: withHistory, label: "توقفوا مؤخراً" },
    { tone: "none", value: data.never_active_students, label: "لم يبدؤوا بعد" },
  ];

  return (
    <>
      <div className="gp-toolbar" style={{ justifyContent: "flex-end" }}>
        <DaysSeg value={days} onChange={setDays} />
      </div>

      <div className="gp-stats">
        <Stat value={data.students_count} label="عدد الطلاب" />
        <Stat value={data.active_students} label={`نشطون خلال ${arDays(days)}`} />
        <Stat value={data.inactive_students} label="غير نشطين" />
        <Stat value={fmt(data.average_score)} label="متوسط النقاط للطالب" />
      </div>

      <div className="gp-panel" style={{ padding: 18, marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12, fontSize: 13.5, fontWeight: 600 }}>
          <span>نشاط المجموعة</span>
          <span className="gp-num">{data.activity_rate}%</span>
        </div>
        <ActivityBar segments={segments} />
        <Legend segments={segments} />
      </div>

      <div className="gp-panel" style={{ padding: 18 }}>
        <div style={{ fontSize: 13.5, fontWeight: 600, marginBottom: 14 }}>الطلاب في كل برنامج</div>
        {data.programs.length === 0 ? (
          <p className="gp-muted">لم يبدأ أي طالب في المجموعة أي برنامج حتى الآن.</p>
        ) : (
          data.programs.map((p) => (
            <div key={p.program} style={{ marginBottom: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 6 }}>
                <span>{p.program_label}</span>
                <span className="gp-muted">
                  {p.students_started} من {data.students_count} طالب، {fmt(p.total_score)} نقطة
                </span>
              </div>
              <ProgressBar value={data.students_count ? (p.students_started / data.students_count) * 100 : 0} />
            </div>
          ))
        )}
      </div>
    </>
  );
}

/* ───────────────────────── المتصدرون ───────────────────────── */

function LeaderboardTab({ groupId }) {
  const [limit, setLimit] = useState(10);
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let off = false;
    setRows(null);
    setError("");
    groupService
      .getLeaderboard(groupId, limit)
      .then((res) => !off && setRows(res.data))
      .catch((e) => !off && setError(getErrorMessage(e)));
    return () => {
      off = true;
    };
  }, [groupId, limit]);

  return (
    <>
      <div className="gp-toolbar" style={{ justifyContent: "flex-end" }}>
        <Seg
          label="عدد المتصدرين"
          value={limit}
          onChange={setLimit}
          options={[
            [10, "أفضل 10"],
            [25, "أفضل 25"],
            [50, "أفضل 50"],
          ]}
        />
      </div>
      {error && <div className="gp-error">{error}</div>}
      {!rows && !error ? (
        <Spinner />
      ) : rows && rows.length === 0 ? (
        <div className="gp-panel">
          <EmptyState title="لا يوجد طلاب" hint="أضف طلاباً للمجموعة ليظهر ترتيبهم هنا." />
        </div>
      ) : (
        rows && (
          <div className="gp-panel gp-table">
            {rows.map((r) => (
              <div className="gp-rank" key={r.id}>
                <span className={`gp-rank-n ${r.rank <= 3 ? "top" : ""}`}>{r.rank}</span>
                <div className="gp-who">
                  <Avatar name={r.full_name} size={34} />
                  <div style={{ minWidth: 0 }}>
                    <b>{r.full_name}</b>
                    <small>
                      {r.programs_started} برنامج، آخر نشاط {formatRelative(r.last_activity)}
                    </small>
                  </div>
                </div>
                <span className="gp-num" style={{ fontWeight: 700 }}>
                  {fmt(r.total_score)} نقطة
                </span>
              </div>
            ))}
          </div>
        )
      )}
    </>
  );
}

/* ───────────────────────── الخاملون ───────────────────────── */

function InactiveTab({ groupId }) {
  const [days, setDays] = useState(7);
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState(null);
  const [meta, setMeta] = useState({ count: 0, totalPages: 1 });
  const [error, setError] = useState("");

  useEffect(() => {
    let off = false;
    setRows(null);
    setError("");
    groupService
      .getInactive(groupId, { days, page, page_size: 15 })
      .then((res) => {
        if (off) return;
        setRows(res.data);
        setMeta({ count: res.count, totalPages: res.total_pages });
      })
      .catch((e) => {
        if (off) return;
        if (e?.response?.status === 404 && page > 1) setPage(1);
        else setError(getErrorMessage(e));
      });
    return () => {
      off = true;
    };
  }, [groupId, days, page]);

  return (
    <>
      <div className="gp-toolbar" style={{ justifyContent: "space-between" }}>
        <span className="gp-sub" style={{ margin: 0 }}>
          طلاب لم يحلّوا أي سؤال خلال الفترة المحددة. اللي لم يبدؤوا أبداً بيظهروا الأول.
        </span>
        <DaysSeg
          value={days}
          onChange={(d) => {
            setDays(d);
            setPage(1);
          }}
        />
      </div>
      {error && <div className="gp-error">{error}</div>}
      {!rows && !error ? (
        <Spinner />
      ) : rows && rows.length === 0 ? (
        <div className="gp-panel">
          <EmptyState title="كل الطلاب نشطون" hint={`كل طلاب المجموعة حلّوا أسئلة خلال ${arDays(days)}.`} />
        </div>
      ) : (
        rows && (
          <div className="gp-panel gp-table">
            {rows.map((r) => (
              <div className="gp-rank" key={r.id}>
                <div className="gp-who">
                  <Avatar name={r.full_name} size={34} />
                  <div style={{ minWidth: 0 }}>
                    <b>{r.full_name}</b>
                    <small>{r.email}</small>
                  </div>
                </div>
                <span className={`gp-recency ${r.never_active ? "none" : "bad"}`}>
                  {r.never_active ? "لم يبدأ بعد" : `منذ ${arDays(r.days_inactive)}`}
                </span>
              </div>
            ))}
          </div>
        )
      )}
      <Pagination page={page} totalPages={meta.totalPages} onChange={setPage} />
    </>
  );
}

/* ───────────────────────── نقل المجموعة (أدمن) ───────────────────────── */

function TransferModal({ group, onClose, onDone }) {
  const [teachers, setTeachers] = useState(null);
  const [teacherId, setTeacherId] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    groupService
      .getTeachers()
      .then((res) =>
        setTeachers((res.data || []).filter((t) => t.id !== group.teacher_id && t.is_active !== false))
      )
      .catch((e) => {
        setError(getErrorMessage(e));
        setTeachers([]);
      });
  }, [group.teacher_id]);

  const submit = async () => {
    setSaving(true);
    setError("");
    try {
      const res = await groupService.adminTransfer(group.id, Number(teacherId));
      onDone(res.data, res.message);
    } catch (e) {
      setError(getErrorMessage(e));
      setSaving(false);
    }
  };

  return (
    <Modal
      title="نقل المجموعة لمدرس آخر"
      onClose={onClose}
      width={440}
      footer={
        <>
          <button className="gp-btn gp-btn-primary" onClick={submit} disabled={saving || !teacherId}>
            {saving ? "جاري النقل..." : "نقل المجموعة"}
          </button>
          <button className="gp-btn" onClick={onClose} disabled={saving}>
            إلغاء
          </button>
        </>
      }
    >
      {error && <div className="gp-error">{error}</div>}
      <p className="gp-muted" style={{ marginBottom: 14 }}>
        المجموعة حالياً عند {group.teacher_name}. بعد النقل هتظهر للمدرس الجديد بكل طلابها.
      </p>
      {teachers === null ? (
        <Spinner />
      ) : (
        <div className="gp-field">
          <label htmlFor="gp-teacher">المدرس الجديد</label>
          <select
            id="gp-teacher"
            className="gp-select"
            style={{ width: "100%" }}
            value={teacherId}
            onChange={(e) => setTeacherId(e.target.value)}
          >
            <option value="">اختر مدرساً</option>
            {teachers.map((t) => (
              <option key={t.id} value={t.id}>
                {t.full_name}
              </option>
            ))}
          </select>
        </div>
      )}
    </Modal>
  );
}

/* ───────────────────────── الصفحة ───────────────────────── */

const TEACHER_TABS = [
  ["students", "الطلبة"],
  ["quizzes", "الاختبارات"],
  ["overview", "نظرة عامة"],
  ["leaderboard", "المتصدرون"],
  ["inactive", "الخاملون"],
];

export default function GroupDetails() {
  useGroupStyles();
  const { groupId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const isAdmin = user?.user_type === "admin";
  const [toastNode, notify] = useToast();

  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("students");
  const [modal, setModal] = useState(null); // "edit" | "transfer" | "delete"
  const [busy, setBusy] = useState(false);

  const loadGroup = useCallback(async () => {
    try {
      const res = isAdmin ? await groupService.adminGetGroup(groupId) : await groupService.getGroup(groupId);
      setGroup(res.data);
      setError("");
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [groupId, isAdmin]);

  useEffect(() => {
    setLoading(true);
    setTab("students");
    loadGroup();
  }, [loadGroup]);

  const doDelete = async () => {
    setBusy(true);
    try {
      if (isAdmin) await groupService.adminDeleteGroup(groupId);
      else await groupService.deleteGroup(groupId);
      navigate("/dashboard/groups", { replace: true });
    } catch (e) {
      notify(getErrorMessage(e), "err");
      setModal(null);
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="gp-root">
        <Spinner />
      </div>
    );
  }
  if (!group) {
    return (
      <div className="gp-root">
        <div className="gp-error">{error || "المجموعة غير موجودة"}</div>
        <Link className="gp-btn" to="/dashboard/groups">
          <ArrowRight size={14} /> العودة للمجموعات
        </Link>
      </div>
    );
  }

  // الأدمن يشوف الطلبة والاختبارات بس
  const tabs = isAdmin
    ? TEACHER_TABS.filter(([key]) => key === "students" || key === "quizzes")
    : TEACHER_TABS;

  return (
    <div className="gp-root">
      <div className="gp-head">
        <div>
          <Link to="/dashboard/groups" className="gp-btn gp-btn-sm" style={{ marginBottom: 14 }}>
            <ArrowRight size={14} /> المجموعات
          </Link>
          <h1 className="gp-title">{group.name}</h1>
          {group.description && <p className="gp-sub">{group.description}</p>}
          <div className="gp-actions" style={{ marginTop: 10 }}>
            <span className={`gp-badge ${group.is_active ? "ok" : ""}`}>{group.is_active ? "نشطة" : "متوقفة"}</span>
            <span className="gp-badge">{group.students_count} طالب</span>
            {isAdmin && <span className="gp-badge">المدرس: {group.teacher_name}</span>}
          </div>
        </div>
        <div className="gp-actions">
          {isAdmin ? (
            <button className="gp-btn" onClick={() => setModal("transfer")}>
              <ArrowLeftRight size={15} /> نقل لمدرس آخر
            </button>
          ) : (
            <button className="gp-btn" onClick={() => setModal("edit")}>
              <Pencil size={15} /> تعديل
            </button>
          )}
          <button className="gp-btn gp-btn-danger" onClick={() => setModal("delete")}>
            <Trash2 size={15} /> حذف
          </button>
        </div>
      </div>

      {tabs.length > 1 && (
        <div className="gp-tabs" role="tablist">
          {tabs.map(([key, label]) => (
            <button key={key} role="tab" className="gp-tab" aria-selected={tab === key} onClick={() => setTab(key)}>
              {label}
            </button>
          ))}
        </div>
      )}

      {tab === "students" && (
        <StudentsTab groupId={groupId} isAdmin={isAdmin} onChanged={loadGroup} notify={notify} />
      )}
      {tab === "quizzes" && <GroupQuizzesTab groupId={group.id} isAdmin={isAdmin} notify={notify} />}
      {tab === "overview" && <OverviewTab groupId={groupId} />}
      {tab === "leaderboard" && <LeaderboardTab groupId={groupId} />}
      {tab === "inactive" && <InactiveTab groupId={groupId} />}

      {modal === "edit" && (
        <GroupFormModal
          group={group}
          onClose={() => setModal(null)}
          onSaved={(_g, msg) => {
            setModal(null);
            notify(msg || "تم الحفظ بنجاح");
            loadGroup();
          }}
        />
      )}
      {modal === "transfer" && (
        <TransferModal
          group={group}
          onClose={() => setModal(null)}
          onDone={(updated, msg) => {
            setModal(null);
            setGroup(updated);
            notify(msg || "تم نقل المجموعة");
          }}
        />
      )}
      {modal === "delete" && (
        <ConfirmDialog
          danger
          title="حذف المجموعة"
          message={`هتتحذف مجموعة "${group.name}" وتتشال عضوية الطلاب منها. حسابات الطلاب وتقدمهم مش هيتأثروا.`}
          confirmLabel="حذف المجموعة"
          loading={busy}
          onConfirm={doDelete}
          onCancel={() => setModal(null)}
        />
      )}
      {toastNode}
    </div>
  );
}